/**
 * Sign-in, and the permission manifest the whole UI is gated on.
 *
 * The login flow is TWO screens, matching the backend as it is today:
 *   1. pick a school (`/api/v1/schools/lookup?q=`), because one deployment
 *      serves many establishments and the sign-in is org-scoped;
 *   2. enter credentials.
 *
 * Which identifier works depends on which of the two login endpoints answers:
 * the org-scoped one (`/orgs/{id}/auth/login`, reached once a school is chosen)
 * matches email / UUID / login_id, while the global `/auth/login` matches
 * phone / UUID / login_id. Both are tried, in that order — the legacy SPA does
 * the same, and a user who types the "wrong" one still gets in.
 */
import { ApiError, request, setBearerToken } from "@/api/client";

export type School = {
  org_id: string;
  name: string;
  code?: string | null;
  country?: string | null;
};

export type UiManifest = {
  /** Permission codes this user effectively holds (assignment ∩ enabled blocks). */
  permissions: string[];
  /** Blocks installed and enabled for the school. */
  enabled_blocks?: string[];
  /** Pages the backend says this user may open, with the codes each needs. */
  pages?: { id: string; perms?: string[] }[];
  feature_flags?: Record<string, unknown>;
  payment_mode?: string;
};

export type CurrentUser = {
  id: string;
  full_name?: string | null;
  role?: string | null;
  organization_id?: string | null;
  /**
   * The school this session belongs to, from the server.
   *
   * The header used to take the name from sessionStorage alone, which is the
   * school you PICKED on the login screen — a different fact, and one that is
   * gone the moment storage is cleared or the PWA is opened on another device.
   * An authenticated user then saw the generic "Dalan" above their own
   * school's data. /api/v1/me has carried this all along.
   */
  organization_name?: string | null;
};

const SELECTED_SCHOOL_ID = "dalan.selectedSchoolId";
const SELECTED_SCHOOL_NAME = "dalan.selectedSchoolName";

export function rememberSchool(school: School) {
  try {
    sessionStorage.setItem(SELECTED_SCHOOL_ID, school.org_id);
    sessionStorage.setItem(SELECTED_SCHOOL_NAME, school.name);
  } catch {
    /* storage disabled — the unscoped login still works */
  }
}

export function selectedSchool(): { id: string; name: string } | null {
  try {
    const id = sessionStorage.getItem(SELECTED_SCHOOL_ID);
    if (!id) return null;
    return { id, name: sessionStorage.getItem(SELECTED_SCHOOL_NAME) ?? "" };
  } catch {
    return null;
  }
}

export function forgetSchool() {
  try {
    sessionStorage.removeItem(SELECTED_SCHOOL_ID);
    sessionStorage.removeItem(SELECTED_SCHOOL_NAME);
  } catch {
    /* ignore */
  }
}

/** The public pre-login lookup. Rate-limited server-side; 404 means "no match",
 *  which is a normal answer here, not a failure. */
export async function lookupSchools(query: string, signal?: AbortSignal): Promise<School[]> {
  if (query.trim().length < 3) return [];
  try {
    const data = await request<{ schools?: School[] }>(
      `/api/v1/schools/lookup?q=${encodeURIComponent(query.trim())}`,
      { signal },
    );
    return data.schools ?? [];
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return [];
    throw error;
  }
}

type LoginResponse = { access_token?: string; token_type?: string };

async function postLogin(path: string, identifier: string, password: string) {
  // OAuth2-password shape: the endpoint takes form fields, not JSON.
  const form = new FormData();
  form.set("username", identifier);
  form.set("password", password);
  return request<LoginResponse>(path, { method: "POST", form });
}

export async function signIn(identifier: string, password: string): Promise<void> {
  const school = selectedSchool();
  const attempts = school
    ? [`/api/v1/orgs/${school.id}/auth/login`, "/api/v1/auth/login"]
    : ["/api/v1/auth/login"];

  let lastError: unknown = null;
  for (const path of attempts) {
    try {
      const data = await postLogin(path, identifier, password);
      // Web/PWA: the server also set the HttpOnly cookie and we ignore this
      // token. Native: it is the only rail we have.
      if (data.access_token) setBearerToken(data.access_token);
      return;
    } catch (error) {
      // Only a rejected credential is worth retrying on the other endpoint;
      // a network failure or a 500 is not.
      if (error instanceof ApiError && (error.status === 401 || error.status === 404)) {
        lastError = error;
        continue;
      }
      throw error;
    }
  }
  throw lastError ?? new ApiError("Identifiants incorrects.", 401);
}

export async function signOut(): Promise<void> {
  setBearerToken(null);
  forgetSchool();
  try {
    await request("/api/v1/auth/logout", { method: "POST" });
  } catch {
    // A failed logout must never trap the user in a session they asked to end.
  }
}

export function fetchCurrentUser(signal?: AbortSignal) {
  return request<CurrentUser>("/api/v1/me", { signal });
}

export function fetchManifest(signal?: AbortSignal) {
  return request<UiManifest>("/api/v1/me/ui", { signal });
}
